import { Component, ChangeDetectionStrategy, DestroyRef, OnInit, ViewEncapsulation, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivityEntityName, AIAgentAttachment, Attachment, ProcessCreateFromAICommandResponse, AIProcessInboxMetadataInfo } from '@jms/model';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { AISettingsService } from '@workbuddy/ai/ai.settings.service';
import { ButtonComponent, ButtonCheckboxComponent, ButtonDisplayStyle, ButtonAccent } from '@workbuddy/ui/v2/components/button';
import { AgentUtility } from '../../agent.utility';

/** Input shape matching the proposeBillScan tool call args */
export interface BillScanToolInput {
  fileIndexes: number[];
  attachmentIds?: string[];
  jobId: string;
  stageId: string;
  jobName?: string;
  stageName?: string;
}

/** Enriched file with pre-computed display values */
interface EnrichedFile {
  file: AIAgentAttachment;
  index: number;
  isImage: boolean;
  thumbnailUrl: string;
  displayName: string;
  iconClass: string;
  typeLabel: string;
}

/**
 * Bill Scan Card Component (Agent theme)
 *
 * Interactive generative UI card rendered when the agent calls `proposeBillScan`.
 * Shows file thumbnails from the conversation, pre-checks the agent's suggested files,
 * and lets the user confirm/adjust selection before triggering bill processing.
 */
@Component({
  selector: 'agent-bill-scan-card',
  standalone: true,
  imports: [ButtonComponent, ButtonCheckboxComponent],
  templateUrl: './bill.scan.card.component.html',
  styleUrls: ['./bill.scan.card.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentBillScanCardComponent implements OnInit {

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonAccent = ButtonAccent;

  private readonly entityDataService = inject(EntityDataServiceRaw);
  private readonly aiSettings = inject(AISettingsService);
  private readonly destroyRef = inject(DestroyRef);

  // Inputs from the tool call
  protected readonly $toolInput = input.required<BillScanToolInput>({ alias: 'toolInput' });

  // All files from the conversation (passed from parent)
  protected readonly $conversationFiles = input.required<AIAgentAttachment[]>({ alias: 'conversationFiles' });

  // Blob URL map for resolving thumbnails
  protected readonly $blobUrls = input.required<Record<string, string>>({ alias: 'blobUrls' });

  // Persisted card action state from a previous session (via callProviderMetadata round-trip)
  protected readonly $initialStatus = input<'success' | 'declined' | undefined>(undefined, { alias: 'initialStatus' });

  // State
  protected readonly $selectedIndexes = signal<Set<number>>(new Set());
  protected readonly $status = signal<'idle' | 'processing' | 'success' | 'error' | 'declined'>('idle');
  protected readonly $errorMessage = signal<string>('');

  // Output events
  protected readonly scanComplete = output<{ success: boolean; message: string }>();
  protected readonly scanDeclined = output<void>({ alias: 'decline' });

  // Derived state
  protected readonly $selectedCount = computed(() => this.$selectedIndexes().size);
  protected readonly $isCompleted = computed(() => this.$status() === 'success' || this.$status() === 'declined');

  /** Pre-compute display values for all conversation files */
  protected readonly $enrichedFiles = computed<EnrichedFile[]>(() => {

    const files = this.$conversationFiles();
    const blobUrls = this.$blobUrls();

    return files.map((file, index) => {
      const displayName = file.filename ? AgentUtility.displayFilenameFromRaw(file.filename) : 'file';
      const classification = AgentUtility.classifyFile(file.mediaType, displayName);
      return {
        file,
        index,
        isImage: AgentUtility.isImageType(file.mediaType),
        thumbnailUrl: file.blobUrl ?? blobUrls[file.id] ?? file.url,
        displayName,
        iconClass: classification.icon,
        typeLabel: classification.label,
      };
    });
  });

  /** Enriched files that are currently selected */
  protected readonly $selectedEnrichedFiles = computed(() => {

    const enriched = this.$enrichedFiles();
    const indexes = this.$selectedIndexes();
    return enriched.filter(e => indexes.has(e.index));
  });

  protected readonly $canScan = computed(() => {

    return this.aiSettings.$loaded() && this.aiSettings.$billImportEnabled() && this.$selectedCount() > 0 && this.$status() !== 'processing';
  });

  public ngOnInit(): void {

    // Restore card state from a previous session (persisted via callProviderMetadata)
    const initialStatus = this.$initialStatus();
    if (initialStatus) {
      this.$status.set(initialStatus);
    }

    const availableFiles = this.$conversationFiles();
    const attachmentIds = this.$toolInput().attachmentIds ?? [];

    if (attachmentIds.length > 0) {
      const selected = availableFiles.reduce((set, file, index) => {
        if (attachmentIds.includes(file.id)) set.add(index);
        return set;
      }, new Set<number>());
      this.$selectedIndexes.set(selected);
      return;
    }

    // Fallback for older proposal cards persisted before attachmentIds existed
    const fileIndexes = this.$toolInput().fileIndexes;
    if (fileIndexes?.length > 0) {
      this.$selectedIndexes.set(new Set(fileIndexes.filter(index => index >= 0 && index < availableFiles.length)));
    }
  }

  /** Toggle selection of a file by index */
  protected toggleFile(index: number): void {

    this.$selectedIndexes.update(set => {
      const next = new Set(set);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  /** Decline the bill scan proposal */
  protected onDeclineClick(): void {

    this.$status.set('declined');
    this.scanDeclined.emit();
  }

  /** Trigger the bill scan process via the ProcessCreateFromAI entity command */
  protected onScanClick(): void {

    if (!this.$canScan()) return;

    this.$status.set('processing');
    this.$errorMessage.set('');

    const toolInput = this.$toolInput();
    const files = this.$conversationFiles();
    const blobUrls = this.$blobUrls();

    // Build Attachment[] from selected conversation files
    const attachments: Attachment[] = [];
    for (const idx of this.$selectedIndexes()) {
      const file = files[idx];
      if (!file) continue;

      const resolvedUrl = file.blobUrl ?? blobUrls[file.id] ?? file.url;
      if (!resolvedUrl) continue;

      const originalName = file.filename ? AgentUtility.displayFilenameFromRaw(file.filename) : 'file';

      attachments.push({
        _id: file.id,
        title: originalName,
        fileName: originalName,
        type: AgentUtility.isImageType(file.mediaType) ? 'Image' : 'File',
        path: resolvedUrl,
      });
    }

    if (attachments.length === 0) {
      this.$status.set('error');
      this.$errorMessage.set('None of the selected files are available. Please choose another file and try again.');
      return;
    }

    // Build metadata linking this scan to a job/stage
    const metadata: AIProcessInboxMetadataInfo = {
      job: { _id: toolInput.jobId, name: toolInput.jobName },
      stage: { _id: toolInput.stageId, name: toolInput.stageName },
    };

    // Execute the ProcessCreateFromAI command via entity framework
    this.entityDataService.executeCommand<ProcessCreateFromAICommandResponse>(ActivityEntityName.Bill, 'ProcessCreateFromAI', { files: attachments, metadata }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        if (!result?.success && result?.fileCount === 0) {
          this.$status.set('error');
          this.$errorMessage.set('All files were invalid or could not be processed.');
          return;
        }
        this.$status.set('success');
        this.scanComplete.emit({ success: true, message: `Bill processing initiated for ${toolInput.jobName ?? toolInput.jobId}` });
      },
      error: (err: unknown) => {
        const message = (err as { error?: { message?: string } })?.error?.message ?? 'Bill scan failed. Please try again.';
        this.$status.set('error');
        this.$errorMessage.set(message);
      },
    });
  }
}
